import { Box, Button, Typography } from '@mui/material'
import styled from '@emotion/styled/macro'
import { DataStatus } from 'models'
import theme from 'theme'
import { CHART_HEIGHT } from './chart-utils'
import { disabledStyles } from 'ui/components/buttons'

const CHART_FOOTER_HEIGHT = 100
const CHART_HEADER_HEIGHT = 39

export const DataTrendsContainer = styled(Box)<{
  status?: DataStatus
  severity: 'healthy' | 'problematic'
  zone: string
  isPredictionMode?: string
}>`
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.medium}px;
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 211px);
  background: ${theme.colors.pageBackground};
  gap: ${theme.spacing.medium}px;

  .chart-container {
    box-sizing: border-box;
    width: 100%;
    height: ${CHART_HEIGHT + CHART_FOOTER_HEIGHT + CHART_HEADER_HEIGHT + 2}px;
    padding-top: ${CHART_HEADER_HEIGHT}px;
    border-radius: ${theme.attributes.borderRadius.quaternary};
    background: ${theme.colors.surfaceContainerBackground};

    ${({ isPredictionMode }) =>
      isPredictionMode &&
      isPredictionMode === 'true' &&
      `
        background: #392b39;
    `};

    .chart-footer {
      width: 100%;
      height: ${CHART_FOOTER_HEIGHT + 8}px;
      background: ${theme.colors.surfaceContainerBackground};
      border-radius: 16px;
      padding: 0 0 24px;
      display: flex;
      align-items: flex-end;
      justify-content: center;

      ${({ isPredictionMode }) =>
        isPredictionMode &&
        isPredictionMode === 'true' &&
        `
        background: #392b39;
    `};

      .legend-wrapper {
        display: flex;
        align-items: center;
        gap: ${theme.spacing.large}px;

        .legend-item {
          display: flex;
          align-items: center;
          gap: ${theme.spacing.xSmall}px;

          .MuiTypography-root {
            color: ${theme.colors.foreground};
            font-family: ${theme.typography.fontFamily.primary};
            font-size: ${theme.typography.fontSize.large};
            font-style: normal;
            font-weight: ${theme.typography.fontWeight.bold};
            line-height: 22px;
            letter-spacing: 0.5px;
          }
        }
      }
    }

    .trends-d3-wrapper {
      position: relative;
      width: 100%;
      height: ${CHART_HEIGHT}px;
      background: ${theme.colors.surfaceContainerBackground};

      .svg-d3 {
        overflow: hidden;
        background: ${theme.colors.surfaceContainerBackground};

        ${({ isPredictionMode }) =>
          isPredictionMode &&
          isPredictionMode === 'true' &&
          `
            background: #392b39;
        `};

        .event-indicator-count {
          cursor: pointer;
        }

        .add-event-button {
          cursor: pointer;
          display: none;

          &:hover {
            .add-button-circle {
              fill: ${theme.colors.cta} !important;
              stroke: ${theme.colors.cta} !important;
            }

            rect {
              fill: ${theme.colors.surfaceContainerBackground};
            }
          }
        }

        .add-button-tooltip {
          display: none;
        }

        .count-tooltip {
          display: none;
        }

        .sla {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: ${theme.spacing.small}px;
          background: rgba(208, 188, 255, 0.16);
          box-shadow: ${theme.attributes.boxShadow.selectFieldPaper};
          border: 0 solid ${theme.colors.controlBorder};
          border-radius: ${theme.attributes.borderRadius.quaternary};
          backdrop-filter: blur(6px) brightness(0.8) contrast(0.9) hue-rotate(320deg);
          z-index: 1000000;

          .sla-tooltip-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: ${theme.spacing.small}px;
            background: rgba(208, 188, 255, 0.16);
            box-shadow: ${theme.attributes.boxShadow.selectFieldPaper};
            border: 0 solid ${theme.colors.controlBorder};
            border-radius: ${theme.attributes.borderRadius.quaternary};
            backdrop-filter: blur(6px) brightness(0.8) contrast(0.9) hue-rotate(320deg);
            z-index: 1000000;
          }
        }
      }

      .data-point-tooltip {
        display: none;
        position: absolute;
        /* The cursor must never enter the tooltip — that fires the chart's mouseout and the tooltip flickers. */
        pointer-events: none;
        opacity: 1;
        color: ${theme.colors.foreground};
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.xLarge};
        font-style: normal;
        font-weight: ${theme.typography.fontWeight.bold};
        line-height: 24px;
        letter-spacing: 0.1px;
        z-index: 10000;
        padding: ${theme.spacing.small}px;
        gap: ${theme.spacing.md}px;
        border-radius: ${theme.attributes.borderRadius.tertiary};
        border: 1px solid ${theme.colors.menuItemHover};
        background: ${theme.colors.surfaceContainerBackground};
        box-shadow: ${theme.colors.legacyChartTooltipShadow};
        min-width: 212px;
        width: fit-content;
        height: 108px;
        justify-content: center;
        flex-direction: column;
        align-items: flex-start;

        &.cpi-061 {
          height: 200px;
        }

        /* +28px per Figma: 16px title row + one 12px gap */
        &.has-state-title {
          height: 136px;
        }

        &.cpi-061.has-state-title {
          height: 228px;
        }

        .tooltip-state-title {
          color: ${theme.colorTokens.info.attention};
          font-family: ${theme.typography.fontFamily.primary};
          font-size: ${theme.typography.fontSize.medium};
          font-style: normal;
          font-weight: ${theme.typography.fontWeight.bold};
          line-height: 16px;
          margin: 0;

          html[data-theme='light'] & {
            color: #976c00;
          }
        }

        .prediction-text-wrapper {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          width: 232px;
          gap: ${theme.spacing.xSmall}px;
          margin-top: -${theme.spacing.half}px;

          .tooltip-performance {
            font-family: ${theme.typography.fontFamily.primary};
            font-size: ${theme.typography.fontSize.medium};
            font-weight: ${theme.typography.fontWeight.bold};
            color: ${theme.colors.calendarDay};
            font-style: normal;
            line-height: 16px;
          }

          svg {
            margin-left: -${theme.spacing.xsm}px;
          }
        }

        .tooltip-units-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 232px;

          .tooltip-units-prediction {
            font-family: ${theme.typography.fontFamily.primary};
            font-size: ${theme.typography.fontSize.medium};
            font-weight: ${theme.typography.fontWeight.bold};
            color: ${theme.colors.calendarDay};
            font-style: normal;
            line-height: 16px;
            margin-top: 0;
            margin-bottom: 0;
          }
        }

        .tooltip-date {
          color: ${theme.colors.foreground};
          font-family: ${theme.typography.fontFamily.primary};
          font-size: ${theme.typography.fontSize.large};
          font-style: normal;
          font-weight: ${theme.typography.fontWeight.extraThick};
          line-height: 20px;
          margin: 0;
        }

        .tooltip-performance,
        .tooltip-units {
          font-family: ${theme.typography.fontFamily.primary};
          font-size: ${theme.typography.fontSize.medium};
          font-weight: ${theme.typography.fontWeight.bold};
          color: ${theme.colors.calendarDay};
          font-style: normal;
          line-height: 16px;
          margin: 0;
        }

        .tooltip-performance {
          color: ${theme.colors.calendarDay};
          font-family: ${theme.typography.fontFamily.primary};
          font-size: ${theme.typography.fontSize.medium};
          font-style: normal;
          font-weight: ${theme.typography.fontWeight.bold};
          line-height: 16px;

          &.prediction-date {
            color: ${theme.colors.foreground};
            font-family: ${theme.typography.fontFamily.primary};
            font-size: ${theme.typography.fontSize.large};
            font-style: normal;
            font-weight: ${theme.typography.fontWeight.extraThick};
            line-height: 20px;
          }
        }

        .tooltip-units {
          margin: 0;
        }

        .severity-text {
          text-transform: capitalize;
        }

        html[data-theme='light'] & .tooltip-value {
          color: ${theme.colors.foreground};
          font-weight: ${theme.typography.fontWeight.extraThick};
        }
      }
    }
  }

  .chart-container::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 0 rgba(0, 0, 0, 0);
    background: #2c242c;
  }

  .chart-container::-webkit-scrollbar-thumb {
    height: 5px;
    background-color: #524256;
  }

  .chart-container::-webkit-scrollbar-thumb:hover {
    background-color: #524256;
  }

  .chart-container::-webkit-scrollbar:vertical {
    display: none;
  }
`

export const DataTrendsControlsContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.medium}px;
`

export const ControlsSection = styled(Box)<{ predictionModalOpen?: string }>`
  display: flex;
  flex-flow: column wrap;
  justify-content: center;
  align-items: flex-start;
  gap: ${theme.spacing.xSmall}px;

  .section-title {
    font-family: ${theme.typography.fontFamily.primary};
    font-weight: ${theme.typography.fontWeight.normal};
    font-size: ${theme.typography.fontSize.large};
    white-space: nowrap;
    color: ${theme.colors.white};
    font-style: normal;
    line-height: 22px;
  }

  .MuiFormControl-root {
    border-color: ${theme.colors.dropdownBorder};
  }
`

// Top-of-page bar that holds the Data Trends filter row (left) and the OnyxAI predict
// button (right). Sits above the summary cards so filters precede the data they drive.
export const ControlsBar = styled(Box)`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacing.medium}px;
  flex-wrap: wrap;
`

export const ResetAllButton = styled(Button)`
  text-transform: none;
  min-width: auto;
  padding: 0;
  align-self: center;
  color: ${theme.colors.textPrimary};
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.bold};
  line-height: 22px;
  white-space: nowrap;

  &:hover {
    background: transparent;
    text-decoration: underline;
  }
`

export const DescriptionWrapper = styled(Box)<{
  status?: DataStatus
  zone: string
}>`
  display: flex;
  justify-content: stretch;
  align-items: center;
  width: 100%;
  height: 162px;
  gap: ${theme.spacing.medium}px;

  .box-wrapper {
    border-radius: ${theme.attributes.borderRadius.tertiary};
    background: #281e28;
    padding: ${theme.spacing.small}px;

    .measurement-wrapper {
      width: 100%;
      display: flex;
      align-items: center;
      gap: ${theme.spacing.small}px;
      padding: 0 ${theme.spacing.small}px;

      .status {
        display: flex;
        align-items: flex-end;

        span {
          font-family: ${theme.typography.fontFamily.primary};
          font-size: ${theme.typography.fontSize.medium};
          font-style: normal;
          font-weight: ${theme.typography.fontWeight.normal};
          line-height: 16px;
          letter-spacing: 0.4px;
        }

        @media (max-width: 1440px) {
          font-size: 2.3rem !important;
          line-height: normal !important;
        }
      }

      .column {
        flex-direction: column;
      }
    }

    .box-wrapper-options {
      display: flex;
      gap: ${theme.spacing.md}px;

      @media (max-width: 1400px) {
        flex-wrap: wrap;
      }
    }

    .label-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      position: relative;
      text-transform: none;

      > div {
        cursor: pointer;
      }

      .title {
        margin-bottom: ${theme.spacing.xSmall}px;
      }

      .count {
        border-radius: ${theme.attributes.borderRadius.large};
        border: 1px solid ${theme.colors.controlBorder};
        background: ${theme.baseColors.info[30]};
        position: absolute;
        top: 30px;
        right: 5px;
        min-width: 14px;
        height: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #66006e;
        text-align: center;
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.mediumSmall};
        font-style: normal;
        font-weight: 700;
        line-height: 16px;
        letter-spacing: 0.5px;
      }
    }
  }

  .wrapper-options {
    flex-direction: column;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    max-width: 679px;
    padding-left: ${theme.spacing.small}px;
    padding-right: ${theme.spacing.small}px;
    padding-bottom: ${theme.spacing.small}px;
    min-width: 529px;
  }

  .wrapper-options,
  .legend-wrapper,
  .wrapper-measurement {
    width: 100%;
  }

  .wrapper-measurement {
    @media (max-width: 1280px) {
      max-width: 266px;
    }

    @media (max-width: 1440px) {
      max-width: 312px;
    }

    @media (min-width: 1441px) {
      max-width: 422px;
    }
  }

  .px-7 {
    padding-left: 7px;
    padding-right: 7px;
  }

  .legend-wrapper {
    padding: ${theme.spacing.small}px;
    max-width: 363px;

    @media (max-width: 1400px) {
      max-width: 187px;
      width: auto;
    }

    @media (max-width: 1280px) {
      max-width: 187px;
    }

    @media (max-height: 800px) {
      padding-top: ${theme.spacing.sm}px;
      padding-bottom: unset;

      .legend-key {
        margin-right: 13px;
      }

      .legend-container {
        gap: 3px !important;
      }
    }

    .legend {
      @media (max-width: 1400px) {
        display: flex;
        flex-direction: row;
        flex-flow: nowrap;
        gap: ${theme.spacing.md}px;
        align-items: start;
        justify-content: center;
      }
    }

    @media screen and (max-width: 1440px) and (max-height: 800px) {
      max-width: 279px;

      .legend {
        flex-flow: column wrap;

        .legend-container {
          flex-flow: row wrap;
          gap: ${theme.spacing.xSmall}px ${theme.spacing.small}px !important;
        }
      }
    }
  }

  .text {
    font-family: ${theme.typography.fontFamily.primary};
    font-weight: ${theme.typography.fontWeight.bold};
    font-size: ${theme.typography.fontSize.large};
    line-height: 20px;
    letter-spacing: 0.1px;
    color: ${theme.colors.white};

    .severity {
      text-transform: capitalize;
    }
  }

  .updated-at {
    font-family: ${theme.typography.fontFamily.primary};
    font-weight: ${theme.typography.fontWeight.bold};
    font-size: ${theme.typography.fontSize.mediumSmall};
    line-height: 16px;
    letter-spacing: 0.5px;
    color: ${theme.baseColors.greyscale[80]};
  }

  .legend {
    width: auto;
    display: flex;
    flex-flow: column wrap;

    .legend-container {
      display: flex;
      flex-wrap: wrap;
      gap: 14px;

      @media (max-width: 1500px) {
        gap: ${theme.spacing.xSmall}px;
      }
    }

    .legend-key {
      font-family: ${theme.typography.fontFamily.primary};
      font-weight: ${theme.typography.fontWeight.bold};
      font-size: ${theme.typography.fontSize.large};
      line-height: 20px;
      letter-spacing: 0.1px;
      color: ${theme.colors.white};
      margin-bottom: ${theme.spacing.xSmall}px;
    }

    .legend-item {
      display: flex;
      align-items: center;

      .MuiTypography-root {
        margin-left: ${theme.spacing.xSmall}px;
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.bold};
        font-size: ${theme.typography.fontSize.medium};
        line-height: 16px;
        letter-spacing: 0.5px;
        color: ${theme.colors.white};
        white-space: nowrap;
      }
    }
  }

  .data-source-wrapper {
    display: flex;
    flex-flow: column wrap;

    .data-source-select-wrapper {
      max-width: 180px;
      margin-top: ${theme.spacing.half}px;
    }

    .title {
      font-family: ${theme.typography.fontFamily.primary};
      font-weight: ${theme.typography.fontWeight.bold};
      font-size: ${theme.typography.fontSize.large};
      line-height: 20px;
      letter-spacing: 0.1px;
      color: ${theme.colors.white}fff;
      margin-bottom: ${theme.spacing.half}px;
    }
  }
`

export const DescriptionSection = styled(Box)<{
  isPredictionMode?: string
}>`
  border-radius: ${theme.attributes.borderRadius.quaternary};
  background: ${theme.colors.surfaceContainerBackground};
  padding: ${theme.spacing.medium}px ${theme.spacing.medium}px ${theme.spacing.small}px;
  width: 100%;
  height: 100%;

  ${({ isPredictionMode }) =>
    isPredictionMode &&
    isPredictionMode === 'true' &&
    `
        background: #392b39;
    `};

  .description-section-inner {
    display: flex;
    flex-flow: column wrap;
    align-items: flex-start;
    justify-content: center;
    gap: ${theme.spacing.md}px;

    .most-recent-performance-wrapper {
      display: flex;
      flex-flow: column wrap;
      align-items: flex-start;
      justify-content: center;
      gap: 2px;

      .most-recent-performance-title {
        display: flex;
        align-items: center;
        gap: ${theme.spacing.medium}px;
      }

      .text {
        color: ${theme.colors.foreground};
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.xLarge};
        font-weight: ${theme.typography.fontWeight.extraThick};
        font-style: normal;
        line-height: 24px;

        span.severity {
          color: #a88ca4;
        }
      }

      .status {
        color: ${theme.colors.white};
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.epic};
        font-style: normal;
        font-weight: ${theme.typography.fontWeight.bold};
        line-height: 48px;
      }

      .updated-at {
        color: ${theme.colors.cardDateText};
        text-align: right;
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.xLarge};
        font-style: normal;
        font-weight: ${theme.typography.fontWeight.bold};
        line-height: 24px;
      }
    }

    .recent-value-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      gap: ${theme.spacing.small}px;
    }

    .last-month-change {
      display: flex;
      align-items: center;
      align-self: flex-end;
      gap: 4px;

      .change-indicator {
        display: flex;
        align-items: center;
        gap: 4px;

        .arrow-icon {
          display: block;
          flex-shrink: 0;
          width: 14px;
          height: 14px;
          stroke-width: 2;

          &.down {
            transform: rotate(180deg);
          }
        }

        .change-text {
          font-family: ${theme.typography.fontFamily.primary};
          font-weight: ${theme.typography.fontWeight.bold};
          font-size: ${theme.typography.fontSize.large};
          line-height: 20px;
        }

        &.positive {
          color: #aed581;
          .change-text {
            color: #aed581;
          }

          html[data-theme='light'] & {
            color: ${theme.colors.statusValueGood};

            .change-text {
              color: ${theme.colors.statusValueGood};
            }
          }
        }

        &.negative {
          color: #e74c3c;
          .change-text {
            color: #e74c3c;
          }
        }
      }

      .change-empty {
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.bold};
        font-size: ${theme.typography.fontSize.large};
        color: ${theme.colors.softMauve};
        line-height: 20px;

        html[data-theme='light'] & {
          color: ${theme.colors.caption};
        }
      }

      .since-label {
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.normal};
        font-size: ${theme.typography.fontSize.large};
        color: ${theme.colors.softMauve};
        line-height: 20px;
        letter-spacing: 0.25px;

        html[data-theme='light'] & {
          color: ${theme.colors.caption};
        }
      }
    }

    .devices-footer {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      width: 100%;
      min-height: 60px;

      .devices-count-text {
        color: ${theme.colors.foreground};
        font-family: ${theme.typography.fontFamily.primary};
        font-size: 40px;
        font-style: normal;
        font-weight: ${theme.typography.fontWeight.bold};
        line-height: 60px;
      }

      .view-devices-button {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        text-transform: none;
        color: ${theme.colors.cta};
        text-align: right;
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.large};
        font-style: normal;
        font-weight: ${theme.typography.fontWeight.extraThick};
        line-height: 22px;

        &:hover {
          color: #c477c1;

          html[data-theme='light'] & {
            color: ${theme.colors.buttonHollowHover};
          }
        }
      }
    }
  }

  .non-ready-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 2px;
    height: 100%;

    .text {
      flex-grow: 1;
      color: ${theme.colors.white};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.xLarge};
      font-weight: ${theme.typography.fontWeight.extraThick};
      font-style: normal;
      line-height: 24px;

      span.severity {
        color: #a88ca4;
      }
    }

    .status-wrapper {
      display: flex;
      align-items: center;
      gap: ${theme.spacing.xSmall}px;

      .status {
        color: ${theme.colors.white};
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.epic};
        font-style: normal;
        font-weight: ${theme.typography.fontWeight.bold};
        line-height: 48px;
        text-transform: capitalize;
      }

      &.not-ready-status {
        .status {
          font-size: 40px;
        }
      }

      #gear {
        animation: ckw 2.5s linear infinite;
        transform-origin: 50% 50%;
        display: inline-block;
      }

      @keyframes ckw {
        0% {
          transform: rotate(0deg);
        }

        100% {
          transform: rotate(360deg);
        }
      }
    }

    html[data-theme='light'] & {
      .text {
        color: ${theme.colors.menuItemText};
      }

      .status-wrapper .status {
        color: ${theme.colors.menuItemText};
      }

      #gear path {
        stroke: ${theme.colors.dropdownLabel};
      }
    }
  }
`

export const LabelsMenuWrapper = styled(Box)<{ isPredictionMode?: string }>`
  border-radius: ${theme.attributes.borderRadius.quaternary};
  background: ${theme.colors.surfaceContainerBackground};
  padding: ${theme.spacing.medium}px ${theme.spacing.medium}px ${theme.spacing.small}px;
  display: flex;
  flex-flow: column wrap;
  justify-content: center;
  align-items: flex-start;
  min-width: 236px;
  width: 236px;
  height: 162px;
  gap: 38px;

  ${({ isPredictionMode }) =>
    isPredictionMode &&
    isPredictionMode === 'true' &&
    `
        background: #392b39;
    `};

  .labels-title {
    color: ${theme.colors.foreground};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xLarge};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.extraThick};
    line-height: 24px;
  }

  .labels-footer {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    width: 100%;

    .labels-count-text {
      color: ${theme.colors.foreground};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: 40px;
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: 60px;
    }

    .labels-dropdown-container {
      padding-bottom: ${theme.spacing.half}px;

      .view-labels-button {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        text-transform: none;
        color: ${theme.colors.cta};
        text-align: right;
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.large};
        font-style: normal;
        font-weight: ${theme.typography.fontWeight.extraThick};
        line-height: 22px;

        &:hover {
          color: #c477c1;

          html[data-theme='light'] & {
            color: ${theme.colors.buttonHollowHover};
          }
        }
      }
    }
  }

  .measurement-wrapper {
    width: 100%;
    min-width: 350px;
    display: flex;
    align-items: center;
    gap: ${theme.spacing.small}px;
  }

  .box-wrapper-options {
    display: flex;
    gap: ${theme.spacing.md}px;

    @media (max-width: 1400px) {
      flex-wrap: wrap;
    }
  }
`

export const StatusValueText = styled(Typography)<{ status?: DataStatus; zone: string; prediction?: string }>`
  font-family: ${theme.typography.fontFamily.primary};
  font-style: normal;
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.statusValueGood};
  margin-top: 0;
  text-transform: capitalize;
  font-size: 40px;
  line-height: 60px;

  ${({ status, zone }) =>
    status &&
    status === DataStatus.Ready &&
    zone === 'green' &&
    `
        color: ${theme.colors.statusValueGood};
      `};

  ${({ status, zone }) =>
    status &&
    status === DataStatus.Ready &&
    zone === 'red' &&
    `
        color: ${theme.colors.statusValueBad};
      `};

  ${({ status }) =>
    status &&
    status === DataStatus.Initializing &&
    `
        color: #FAC364;
      `};

  ${({ status }) =>
    status &&
    status === DataStatus.Recalculating &&
    `
        font-size: 58px;
        line-height: normal;
        color: #FAC364;
      `};

  ${({ prediction }) =>
    prediction &&
    prediction === 'true' &&
    `
        color: #D375FF;
      `};
`

export const OnyxAIWrapper = styled(Box)<{ has_model: string }>`
  // display: flex;
  // flex-direction: column;
  // align-items: center;
  // justify-content: center;
  // border-radius: ${theme.attributes.borderRadius.tertiary};
  // background: #281e28;
  // padding: ${theme.spacing.md}px ${theme.spacing.small}px;
  // width: 100%;
  // max-width: 80px;

  .onyxai-text {
    color: ${theme.colors.white};
    text-align: center;
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 20px;
    letter-spacing: 0.1px;
    margin-top: ${theme.spacing.xSmall}px;
    margin-bottom: 2px;

    ${({ has_model: hasModel }) =>
      hasModel === 'false' &&
      `
      margin-top: 0;
    `};
  }

  .onyxai-btn {
    gap: ${theme.spacing.sm}px;
    padding: ${theme.spacing.sm}px ${theme.spacing.medium}px ${theme.spacing.sm}px ${theme.spacing.small}px;
    border-radius: ${theme.attributes.borderRadius.tertiary};
    color: ${theme.colors.surfaceContainerBackground};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 20px;
    letter-spacing: 0.1px;
    box-shadow: none;

    &.disabled-btn {
      cursor: default;
      background: #392b39;
      color: ${theme.baseColors.greyscale[50]};
      border-color: #392b39;

      svg {
        path {
          fill: ${theme.baseColors.greyscale[50]};
        }
      }
    }
  }

  .not-available-svg {
    margin-top: ${theme.spacing.half}px;

    &:hover {
      circle {
        fill-opacity: 0.12;
        stroke-opacity: 0.4;
      }
    }
  }
`

export const OnyxAITooltipWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${theme.spacing.xSmall}px;

  .tooltip-title {
    color: ${theme.colors.white};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.medium};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.extraThick};
    line-height: 140%;
  }

  .tooltip-text {
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.small};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.extraThick};
    color: ${theme.baseColors.greyscale[80]};
    line-height: 140%;
  }
`

export const PredictionDescriptionWrapper = styled(Box)<{ status?: DataStatus; zone: string }>`
  border-radius: ${theme.attributes.borderRadius.quaternary} ${theme.attributes.borderRadius.quaternary} 0 0;
  background: ${theme.colors.primaryDarker};
  padding: 18px;
  gap: 18px;
  width: 100%;
  height: 119px;
  display: flex;
  align-items: center;

  @media (min-width: 1920px) {
    height: 150px;
  }

  .section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 1px;
    border-radius: ${theme.attributes.borderRadius.tertiary};
    background: #281e28;
    width: 217px;
    height: 99px;
    padding: ${theme.spacing.md}px ${theme.spacing.small}px ${theme.spacing.md}px ${theme.spacing.md}px;

    @media (min-width: 1440px) {
      width: 262px;
      padding: ${theme.spacing.md}px 37px;
    }

    @media (min-width: 1920px) {
      width: 270px;
      height: 114px;
      padding: 19px ${theme.spacing.xLarge}px;
    }

    .recent-performance-value {
      color: ${theme.baseColors.info[20]};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.huge};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.normal};
      margin-bottom: 5px;

      span {
        color: ${theme.baseColors.info[20]};
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.header};
        font-style: normal;
        font-weight: ${theme.typography.fontWeight.normal};
      }

      ${({ status, zone }) =>
        status &&
        zone === 'green' &&
        `
        color: ${theme.baseColors.info[20]};
        
        span {
          color: ${theme.baseColors.info[20]};
        }
      `};

      ${({ status, zone }) =>
        status &&
        zone === 'red' &&
        `
        color: ${theme.baseColors.info[50]} !important;
        
        span {
          color: ${theme.baseColors.info[50]} !important;
        }
      `};
    }

    .predicted-performance-value {
      color: ${theme.baseColors.info[20]};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.huge};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.normal};
      margin-bottom: 5px;

      span {
        color: ${theme.baseColors.info[20]};
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.header};
        font-style: normal;
        font-weight: ${theme.typography.fontWeight.normal};
      }
    }

    .recent-performance-text {
      color: ${theme.baseColors.info[20]};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.large};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: 20px;
      letter-spacing: 0.1px;

      ${({ status, zone }) =>
        status &&
        zone === 'green' &&
        `
        color: ${theme.baseColors.info[20]};
      `};

      ${({ status, zone }) =>
        status &&
        zone === 'red' &&
        `
        color: ${theme.baseColors.info[50]};
      `};
    }

    .updated-at {
      color: ${theme.baseColors.greyscale[80]};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.medium};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.normal};
      line-height: 16px;
      letter-spacing: 0.4px;
      text-transform: capitalize;
    }

    &.section-prediction {
      width: 220px;
      padding: ${theme.spacing.md}px ${theme.spacing.small}px ${theme.spacing.md}px ${theme.spacing.md}px;

      .predicted-performance-value {
        color: #bb95ea;

        span {
          color: #bb95ea;
        }
      }

      .recent-performance-text {
        color: #bb95ea;
      }

      @media (min-width: 1440px) {
        width: 265px;
        padding: ${theme.spacing.md}px 37px;
      }

      @media (min-width: 1920px) {
        width: 267px;
        padding: 19px ${theme.spacing.xLarge}px;
      }
    }

    &.section-legend {
      width: 198px;
      padding: 10.8px ${theme.spacing.small}px;

      @media (min-width: 1440px) {
        width: 232px;
        padding: 10.8px 33px;
      }

      @media (min-width: 1920px) {
        width: 393px;
        padding: 19px 29px 20.4px;
      }

      .legend-title {
        color: ${theme.colors.white};
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.large};
        font-style: normal;
        font-weight: ${theme.typography.fontWeight.bold};
        line-height: 20px;
        letter-spacing: 0.1px;
        margin-bottom: ${theme.spacing.sm}px;
      }

      .legend-wrapper {
        display: flex;
        flex-flow: row wrap;
        width: 100%;
        flex-basis: 100%;
        row-gap: ${theme.spacing.half}px;

        @media (min-width: 1920px) {
          row-gap: ${theme.spacing.sm}px;
        }

        .legend-item {
          display: flex;
          align-items: center;
          flex-basis: 50%;
          gap: ${theme.spacing.xSmall}px;

          .MuiTypography-root {
            color: ${theme.colors.white};
            font-family: ${theme.typography.fontFamily.primary};
            font-size: ${theme.typography.fontSize.medium};
            font-style: normal;
            font-weight: ${theme.typography.fontWeight.bold};
            line-height: 16px;
            letter-spacing: 0.5px;
            white-space: nowrap;
          }
        }
      }
    }

    &.section-controls {
      flex-direction: row;
      gap: 36px;
      justify-content: flex-start;
      width: 459px;
      padding: ${theme.spacing.sm}px 0 21px 17px;

      @media (min-width: 1440px) {
        width: 477px;
        flex-grow: 1;
        padding: ${theme.spacing.sm}px 0 21px 26.5px;
      }

      @media (min-width: 1920px) {
        width: 666px;
        padding: 19px 0 19px 36px;
      }

      .data-source-wrapper {
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing.small}px;

        .title {
          color: ${theme.colors.white};
          font-family: ${theme.typography.fontFamily.primary};
          font-size: ${theme.typography.fontSize.large};
          font-style: normal;
          font-weight: ${theme.typography.fontWeight.bold};
          line-height: 20px;
          letter-spacing: 0.1px;
        }
      }
    }
  }
`

export const predictionModeExitBtnSx = {
  background: `${theme.baseColors.surface[50]} !important`,
  borderColor: `${theme.baseColors.surface[50]} !important`,
  svg: {
    path: {
      fill: `${theme.colors.textPrimary} !important`
    }
  },
  color: `${theme.colors.textPrimary} !important`,
  fontFamily: `${theme.typography.fontFamily.primary} !important`,
  fontSize: `${theme.typography.fontSize.large} !important`,
  fontStyle: 'normal',
  fontWeight: `${theme.typography.fontWeight.bold} !important`,
  lineHeight: '20px',
  letterSpacing: '0.1px'
}

export const predictBtnSx = {
  color: theme.colors.surfaceContainerBackground,

  '&:hover': {
    background: '#C477C1 !important',
    color: theme.colors.surfaceContainerBackground,

    "html[data-theme='light'] &": {
      background: `${theme.colors.buttonHollowHover} !important`
    }
  }
}

export const predictDisabledBtnSx = {
  ...disabledStyles,
  color: theme.colors.surfaceContainerBackground,

  '&:hover': {
    color: theme.colors.surfaceContainerBackground
  }
}
